import React, { useState } from 'react';
import {
  Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem,
  Tooltip, Divider, useTheme,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';

/**
 * RowActions - the universal "last column" of every listings table.
 *
 * Layout:                                   ┌────────────┐
 *                                           │  ⋮    ›   │
 *                                           └────────────┘
 *
 * - The ``›`` (chevron-right) is ALWAYS the primary "Open" action.
 *   The whole row is also clickable, but the chevron is a clear
 *   discovery affordance and the universal target for keyboard
 *   focus.
 * - The ``⋮`` (kebab) appears only when the page provides ``items``
 *   (delete, preview, edit, etc.). The menu pops below the row.
 *
 * Props:
 *   onOpen     - required - called when the chevron (or row) is
 *                clicked. Pass ``null`` to suppress the chevron.
 *   openLabel  - tooltip on the chevron (default "Open")
 *   items      - optional - list of secondary action items:
 *                [{ label, icon, onClick, danger?, divider? }]
 *                Items with ``danger: true`` render in the error
 *                tone. ``divider: true`` inserts a separator.
 */
const RowActions = ({
  onOpen,
  openLabel = 'Open',
  items,
  size = 'small',
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleMenuClose = (e) => {
    if (e) e.stopPropagation?.();
    setAnchorEl(null);
  };

  const hasItems = Array.isArray(items) && items.length > 0;

  return (
    <Box
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
      // Stop propagation so clicks here don't trigger row navigation.
      onClick={(e) => e.stopPropagation()}
    >
      {hasItems && (
        <>
          <Tooltip title="More actions" arrow placement="top">
            <IconButton
              size={size}
              onClick={handleMenuOpen}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.text.primary,
                  bgcolor: 'transparent',
                },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                minWidth: 180,
                boxShadow: theme.palette.boxShadow,
                border: `1px solid ${theme.palette.divider}`,
              },
            }}
          >
            {items.map((item, idx) => {
              if (item.divider) {
                // eslint-disable-next-line react/no-array-index-key
                return <Divider key={`d-${idx}`} sx={{ my: 0.5 }} />;
              }
              const Icon = item.icon;
              return (
                <MenuItem
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${item.label}-${idx}`}
                  disabled={item.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuClose();
                    item.onClick?.(e);
                  }}
                  sx={{
                    fontSize: 13,
                    color: item.danger ? theme.palette.error.main : 'inherit',
                  }}
                >
                  {Icon && (
                    <ListItemIcon sx={{
                      minWidth: 28,
                      color: item.danger ? theme.palette.error.main : 'text.secondary',
                    }}>
                      <Icon fontSize="small" />
                    </ListItemIcon>
                  )}
                  <ListItemText primaryTypographyProps={{ fontSize: 13 }}>
                    {item.label}
                  </ListItemText>
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}

      {onOpen && (
        <Tooltip title={openLabel} arrow placement="top">
          <IconButton
            size={size}
            onClick={(e) => {
              e.stopPropagation();
              onOpen(e);
            }}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.primary.main,
                bgcolor: 'transparent',
              },
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default RowActions;
